import { CalendarIcon } from "lucide-react";
import React, { forwardRef, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";

import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";

import TextInput from "../TextInput/TextInput";

export type DatePickerInputProps = {
  readonly id?: string;
  readonly name: string;
  readonly value: Date | undefined;
  readonly placeholder?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onChange?: (date: Date | undefined) => void;
  readonly onBlur?: () => void;
  readonly onFocus?: () => void;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly readOnly?: boolean;
  readonly minDate?: Date;
  readonly maxDate?: Date;
  readonly "data-testid"?: string;
};

const DatePickerInput = forwardRef<HTMLDivElement, DatePickerInputProps>(
  (props, ref): React.ReactElement => {
    const {
      id,
      name,
      placeholder = "Select date",
      className = "",
      disabled,
      onBlur,
      onFocus,
      onChange,
      value,
      readOnly,
      minDate = new Date(), // Default to today to disable past dates
      maxDate,
      "data-testid": dataTestId,
    } = props;

    const [isOpen, setIsOpen] = useState(false);

    // Create a date for today at midnight to compare with other dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const formatDate = (date: Date | undefined): string => {
      if (!date) return "";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    // Function to disable past dates
    const isDateDisabled = (date: Date): boolean => {
      const dateToCheck = new Date(date);
      dateToCheck.setHours(0, 0, 0, 0);
      return dateToCheck < today;
    };

    const handleDateSelect = (date: Date | undefined) => {
      onChange?.(date);
      setIsOpen(false);
    };

    const handleInputClick = () => {
      if (!disabled && !readOnly) {
        setIsOpen(true);
        onFocus?.();
      }
    };

    const interiorRef = useRef<HTMLDivElement>(null);

    useOutsideClick(interiorRef, () => {
      setIsOpen(false);
    });

    return (
      <div
        className="DatePickerInput relative"
        ref={interiorRef}
        data-testid={dataTestId}
      >
        <TextInput
          name={name}
          type="button"
          aria-invalid={props["aria-invalid"]}
          aria-describedby={props["aria-describedby"]}
          className={cn(
            "h-10",
            "peer block w-full appearance-none rounded border-none px-3 text-left ring-1 ring-light-grey focus:outline-none focus:ring-1 focus:!ring-blue-400 aria-invalid:ring-1 aria-invalid:ring-red aria-invalid:placeholder:text-red",
            "placeholder:!text-secondary",
            className,
            disabled && "pointer-events-none opacity-50"
          )}
          onClick={handleInputClick}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          value={value ? formatDate(value) : placeholder}
          rightIcon={<CalendarIcon className="h-4 w-4 text-primary" />}
        />

        {isOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[260px] rounded-md border border-gray-200 bg-white shadow-lg">
            <DayPicker
              mode="single"
              selected={value}
              onSelect={handleDateSelect}
              disabled={disabled || isDateDisabled}
              defaultMonth={value ? new Date(value) : undefined}
              className="p-3"
              classNames={{
                months: "flex flex-col space-y-4 sm:space-x-4 sm:space-y-0",
                month: "!m-0",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-medium",
                nav: "space-x-1 flex items-center justify-end",
                nav_button:
                  "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                disabled: "text-gray-300 opacity-40 cursor-not-allowed",
                head_row: "flex",
                head_cell:
                  "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                day: "h-9 w-9 p-0 text-center font-normal aria-selected:opacity-100",
                day_selected:
                  "bg-[rgb(var(--repeatmd-brand-color))] text-white hover:bg-[rgb(var(--repeatmd-brand-color))] hover:text-white focus:bg-[rgb(var(--repeatmd-brand-color))] focus:text-white",
                day_today: "bg-[rgb(var(--repeatmd-brand-tint))] font-semibold",
                day_outside:
                  "day-outside text-muted-foreground opacity-50 aria-selected:bg-[rgb(var(--repeatmd-brand-tint))] aria-selected:text-muted-foreground aria-selected:opacity-30",
                day_disabled: "text-gray-300 opacity-40 cursor-not-allowed",
                day_range_middle:
                  "aria-selected:bg-[rgb(var(--repeatmd-brand-tint))] aria-selected:text-accent-foreground",
                day_hidden: "invisible",
                weekday: "h-9 w-9 p-0 aria-selected:opacity-100",
                today:
                  "rounded bg-gray-200 text-black hover:bg-gray-200 hover:text-black focus:bg-gray-200 focus:text-black",
                selected:
                  "rounded bg-[rgb(var(--repeatmd-brand-color))] text-white hover:bg-[rgb(var(--repeatmd-brand-color))] hover:text-white focus:bg-[rgb(var(--repeatmd-brand-color))] focus:text-white",
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

DatePickerInput.displayName = "DatePickerInput";

export default DatePickerInput;
